import React, { useState, FC } from "react";
import { Button } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "../../inputs/ByzMuiTypography";

const ADD_ICON_DEFAULT = "/assets/icons/add/add2.svg";
const ADD_ICON_HOVER = "/assets/icons/add/add.svg";

interface ByzMuiAddButtonProps extends React.ComponentProps<typeof Button> {
  onClick?: () => void;
  link?: string;
  popup?: boolean;
}

const StyledButton = styled(Button)<{ popup?: boolean }>(
  ({ popup, disabled, theme }) => ({
    borderRadius: popup ? "20px" : "4px",
    padding: "4px 16px",
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "2px",
    border: `1px solid ${disabled ? theme.palette.action.disabled : theme.palette.primary.main}`,
    backgroundColor: disabled
      ? alpha(theme.palette.action.disabled, 0.2)
      : alpha(theme.palette.primary.main, 0.1),
    color: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
    cursor: disabled ? 'not-allowed' : 'pointer',
    "&:hover": {
      backgroundColor: disabled ? undefined : theme.palette.primary.main,
      color: disabled ? undefined : theme.palette.common.white,
    },
  })
);

const ByzMuiAddButton: FC<ByzMuiAddButtonProps> = ({
  onClick,
  link,
  popup,
  disabled,
  children,
  ...rest
}) => {
  const { t } = useTranslate();
  const [hover, setHover] = useState(false);

  const buttonContent = (
    <>
      <img
        src={hover ? "/assets/icons/add/add.svg" : "/assets/icons/add/add2.svg"}
        alt="Ekle"
        style={{
          width: "16px",
          height: "16px",
          transition: "0.2s ease-in-out",
        }}
      />
      <ByzMuiTypography
        component="span"
        fontWeight={400}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {t("buttons.add")}
      </ByzMuiTypography>
    </>
  );

  const btn = (
    <StyledButton
      popup={popup}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...rest}
    >
      <img
        src={hover ? ADD_ICON_HOVER : ADD_ICON_DEFAULT}
        alt={t("buttons.add")}
        style={{ width: "16px", height: "16px", transition: "0.2s ease-in-out" }}
      />
      <ByzMuiTypography fontWeight={400} variant="body2" sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        {children || t("buttons.add")}
      </ByzMuiTypography>
    </StyledButton>
  );

  return link ? (
    <Link to={link} style={{ textDecoration: "none" }}>{btn}</Link>
  ) : (
    btn
  );
};

export default ByzMuiAddButton;